"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { authClient, useSession } from "@/lib/auth-client";

async function invalidateImpersonationCaches(
	queryClient: ReturnType<typeof useQueryClient>,
	refetchSession: () => Promise<unknown>,
) {
	await Promise.all([
		queryClient.invalidateQueries({ queryKey: ["userPermissions"] }),
		queryClient.invalidateQueries({ queryKey: ["userMembership"] }),
		queryClient.invalidateQueries({ queryKey: ["user", "securityFlags"] }),
		queryClient.invalidateQueries({ queryKey: ["user"] }),
		queryClient.invalidateQueries({ queryKey: ["pools"] }),
	]);
	queryClient.removeQueries({ queryKey: ["favorites"] });
	await refetchSession();
}

export function useImpersonation() {
	const queryClient = useQueryClient();
	const { refetch: refetchSession } = useSession();
	const [isStarting, setIsStarting] = useState(false);
	const [isStopping, setIsStopping] = useState(false);

	const start = useCallback(
		async (userId: string) => {
			setIsStarting(true);
			try {
				const res = await fetch(`/api/admin/users/${userId}/impersonate`, {
					method: "POST",
					credentials: "include",
				});
				const body = (await res.json().catch(() => null)) as { error?: string } | null;
				if (!res.ok) {
					throw new Error(body?.error ?? "Failed to start impersonation");
				}
				await invalidateImpersonationCaches(queryClient, refetchSession);
			} finally {
				setIsStarting(false);
			}
		},
		[queryClient, refetchSession],
	);

	const stop = useCallback(async (): Promise<{ role?: string | null } | null> => {
		setIsStopping(true);
		try {
			const res = await fetch("/api/admin/stop-impersonating", {
				method: "POST",
				credentials: "include",
			});
			const body = (await res.json().catch(() => null)) as { error?: string } | null;
			if (!res.ok) {
				throw new Error(body?.error ?? "Failed to stop impersonation");
			}
			await invalidateImpersonationCaches(queryClient, refetchSession);
			const restored = await authClient.getSession();
			const role = (restored.data?.user as { role?: string | null } | undefined)?.role ?? null;
			return { role };
		} finally {
			setIsStopping(false);
		}
	}, [queryClient, refetchSession]);

	return {
		start,
		stop,
		isStarting,
		isStopping,
		isPending: isStarting || isStopping,
	};
}
